import { newFeatureFilter, newPropertyFilter } from '../request-filters';
import { newBoundingBoxEvent } from '../bounding-box';
import { Island, Province, Region } from '../../locations';
import { setFeatureStateForSources, whenFeaturesAvailable } from '../features';
import { wrapUpdateCall } from '../api';
import { filterLayerIfExists, removeApproxLocationCircle } from '../layers';
import LocationFeature from '../models/feature';

const handleRegionLayerClick = async ( ctx, event ) => {
    if ( ctx.abortController ) {
        ctx.abortController.abort();
        ctx.abortController = new AbortController();
    }

    const mapFeature = event.detail.feature;

    if ( !mapFeature ) {
        return;
    }

    const feature = LocationFeature.fromMapBoxFeature( mapFeature );

    ctx.setLoading( true );

    ctx.movefuncs = [];

    removeApproxLocationCircle( ctx.map );

    ctx.root.dispatchEvent( new CustomEvent( 'twc:collapse:collapse-all', { bubbles: true } ) );

    ctx.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:remove-area', {
        bubbles: true,
        detail: {
            search: false,
        },
    } ) );

    ctx.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:search-this-area:deactivate', { bubbles: true } ) );

    ctx.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:draw-your-search:deactivate', {
        bubbles: true,
        detail: {
            search: false,
        },
    } ) );

    ctx.locationIndicator.update( feature.label( ctx.language ), {
        autohide: true,
        timeout: 5000,
    } );

    let fitBounds = false;

    let currentZoom = ctx.map.getZoom();

    if ( currentZoom < 6 ) {
        fitBounds = true;
    }

    if ( fitBounds && ctx.options.fitBoundsOnClick ) {
        const bboxOptions = {};
        ctx.map.fitBounds( feature.bbox, bboxOptions, newBoundingBoxEvent() );
    }

    if ( ctx.currentLocation !== null ) {
        ctx.previousLocation = ctx.currentLocation;
    }

    ctx.currentLocation = mapFeature;

    const featureParams = newFeatureFilter( {
        parentType: feature.type,
        parentID: feature.id,
    } );

    const propertyParams = newPropertyFilter( {}, ctx.baseForm );
    propertyParams.set( 'currentLocationID', feature.id );
    propertyParams.set( 'currentLocationType', Region );

    propertyParams.delete( 'longitude' );
    propertyParams.delete( 'latitude' );

    const featureTypeToLoad = feature.nextChildFeatureType();

    const url = new URL( `/${featureTypeToLoad}`, ctx.apiURL );

    const filter = [
        '!=', [ 'get', 'id' ], feature.id,
    ];

    if ( feature.searchSynonymousForLocationType === Province ) {
        setFeatureStateForSources( ctx.map, [ Province ], {
            active: false,
        } );

        filterLayerIfExists( ctx.map, `${Province}:fill`, filter, ctx.debug );
        filterLayerIfExists( ctx.map, `${Province}:outline`, filter, ctx.debug );
    } else {
        filterLayerIfExists( ctx.map, `${Region}:fill`, filter, ctx.debug );
        filterLayerIfExists( ctx.map, `${Region}:outline`, filter, ctx.debug );
    }

    const metaOptions = {
        focusMapToSearch: false,
        tasks: [],
        fetchPropertiesAfterAnimation: true,
    };

    if ( featureTypeToLoad !== Island ) {
        let siblingOptions = () => {
            const bounds = ctx.map.getBounds();

            const siblingFeatureFilter = newFeatureFilter( {
                boundSW: bounds.getSouthWest().lat + ',' + bounds.getSouthWest().lng,
                boundNE: bounds.getNorthEast().lat + ',' + bounds.getNorthEast().lng,
            } );

            let excludeLocationIDs = [];

            const provinceSource = ctx.map.getSource( Province );

            if ( provinceSource ) {
                const provinceFeatures = provinceSource.serialize().data.features || [];
                excludeLocationIDs = [ ...excludeLocationIDs, provinceFeatures.map( ( f ) => parseInt( f.id ) ) ];
            }

            siblingFeatureFilter.set( 'excludeLocationIDs', excludeLocationIDs );

            const siblingOptions = {
                fetchFeatures: true,
                featureSearchURL: new URL( `/${Province}`, ctx.apiURL ),
                featureSearchParams: siblingFeatureFilter,
            };

            const siblingMeta = {
                focusMapToSearch: false,
                appendFeaturesIfLayerExists: true,
            };

            return {
                options: siblingOptions,
                meta: siblingMeta,
            };
        };

        metaOptions.tasks.push( wrapUpdateCall( ctx, siblingOptions ) );
    }

    let layerTypeToUse = featureTypeToLoad;

    if ( layerTypeToUse === Island ) {
        layerTypeToUse = Province;
    }

    whenFeaturesAvailable( ctx.map, {
        source: layerTypeToUse,
        filter: null,
        validate: ctx.debug,
    }, ctx.abortController.signal ).then( ( features ) => {
        const featuresToUpdate = features.filter( ( f ) => {
            return f.properties.regionID === feature.id;
        } );

        for ( let i = 0; i < featuresToUpdate.length; i++ ) {
            ctx.map.setFeatureState(
                {
                    source: layerTypeToUse,
                    id: featuresToUpdate[ i ].id,
                },
                {
                    active: true,
                } );
        }
    } )
        .catch( ( err ) => {
            if ( err.name === 'AbortError' ) {
                return;
            }
        } );

    const regionID = feature.id;

    ctx.movefuncs = [ ( ctx, feature ) => {
        if ( feature.source !== layerTypeToUse || feature.properties.regionID !== regionID ) {
            return;
        }

        const filter = {
            source: layerTypeToUse,
            id: feature.id,
        };

        const state = {
            active: true,
        };

        ctx.map.setFeatureState( filter, state );
    } ];

    const regionFillLayer = ctx.map.getLayer( `${Region}:fill` );

    if ( regionFillLayer ) {
        ctx.map.setLayoutProperty( `${Region}:fill`, 'visibility', 'none', ctx.debug );
        ctx.map.setLayoutProperty( `${Region}:outline`, 'visibility', 'none', ctx.debug );
    }

    let options = {
        fetchFeatures: true,
        featureSearchURL: url,
        featureSearchParams: featureParams,
        fetchProperties: true,
        propertySearchURL: ctx.propertySearchURL,
        propertySearchParams: propertyParams,
        expectProperties: false,
    };

    let results = [];

    try {
        results = await ctx.update( options, metaOptions, ctx.abortController.signal );
    } catch ( err ) {
        if ( err.name === 'AbortError' ) {
            return;
        }
    }

    if ( metaOptions.tasks.length > 0 ) {
        let tasks = [];

        for ( let i = 0; i < metaOptions.tasks.length; i++ ) {
            tasks = [ ...tasks, metaOptions.tasks[ i ] ];
        }

        for ( let t = 0; t < tasks.length; t++ ) {
            const task = tasks[ t ];

            try {
                const result = await task();
                results = [ ...results, ...result ];
            } catch ( err ) {
                if ( err.name === 'AbortError' ) {
                    return;
                }
            }
        }
    }

    ctx.previousSearchState = propertyParams.toString();

    ctx.setLoading( false );

    return results;
};

export {
    handleRegionLayerClick,
};
